import React from "react";
import { CheckCircle, Info, XCircle } from "lucide-react";
import Footer from "../components/layout/Footer";
import Logo from "../components/Logo.jsx";
import SEO from "../components/SEO.jsx";
import { seoPages } from "../content/brandCopy.js";

const License = () => {
  const mitLicense = `MIT License

Copyright (c) 2025 Slabpixel

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
`;

  const allowedUses = [
    {
      icon: <CheckCircle className="w-5 h-5 text-green-500" />,
      title: "Commercial Use",
      description: "Use FlowBitz in commercial projects, client work, and products"
    },
    {
      icon: <CheckCircle className="w-5 h-5 text-green-500" />,
      title: "Modification",
      description: "Modify, adapt, and customize components for your specific needs"
    },
    {
      icon: <CheckCircle className="w-5 h-5 text-green-500" />,
      title: "Distribution",
      description: "Distribute and share FlowBitz with others"
    },
    {
      icon: <CheckCircle className="w-5 h-5 text-green-500" />,
      title: "Private Use",
      description: "Use FlowBitz in private projects and internal applications"
    },
    {
      icon: <CheckCircle className="w-5 h-5 text-green-500" />,
      title: "Patent Use",
      description: "Use any patents held by the contributors"
    },
    {
      icon: <CheckCircle className="w-5 h-5 text-green-500" />,
      title: "Sublicensing",
      description: "Sublicense FlowBitz under different terms"
    }
  ];

  const requirements = [
    {
      icon: <Info className="w-5 h-5 text-blue-500" />,
      title: "License & Copyright Notice",
      description: "Include the MIT license and copyright notice in all copies"
    },
    {
      icon: <Info className="w-5 h-5 text-blue-500" />,
      title: "Source Code",
      description: "Include the source code when distributing modified versions"
    }
  ];

  const restrictions = [
    {
      icon: <XCircle className="w-5 h-5 text-red-500" />,
      title: "No Warranty",
      description: "The software is provided 'as is' without any warranty"
    },
    {
      icon: <XCircle className="w-5 h-5 text-red-500" />,
      title: "No Liability",
      description: "Authors are not liable for any damages or issues"
    }
  ];

  const thirdPartyLicenses = [
    {
      name: "GSAP (GreenSock)",
      version: "3.13.0",
      license: "Commercial License",
      description: "Animation library - bundled with FlowBitz",
      note: "GSAP's commercial license is included with FlowBitz"
    },
    {
      name: "Three.js",
      version: "0.180.0",
      license: "MIT",
      description: "3D graphics library - auto-loaded when needed",
      note: "Auto-loaded only for shape-blur component"
    }
  ];

  const faq = [
    {
      question: "Can I use FlowBitz in commercial projects?",
      answer: "Yes! The MIT license allows commercial use without any restrictions. You can use FlowBitz in client projects, commercial websites, and even sell products that include FlowBitz."
    },
    {
      question: "Do I need to pay for FlowBitz?",
      answer: "No, FlowBitz is completely free to use. The MIT license allows free use for both personal and commercial projects."
    },
    {
      question: "Can I modify FlowBitz components?",
      answer: "Absolutely! You can modify, customize, and adapt FlowBitz components to fit your specific needs. The MIT license encourages modification and improvement."
    },
    {
      question: "Do I need to include the license in my project?",
      answer: "Yes, when distributing your project (especially if modified), you should include the MIT license and copyright notice. This is a simple requirement that protects both you and the original authors."
    },
    {
      question: "Can I redistribute FlowBitz?",
      answer: "Yes, you can redistribute FlowBitz as-is or with modifications. Just make sure to include the license and copyright notice."
    },
    {
      question: "What about GSAP's license?",
      answer: "GSAP's commercial license is included with FlowBitz, so you don't need a separate GSAP license when using FlowBitz. However, if you use GSAP directly in your project outside of FlowBitz, you may need your own license."
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "FlowBitz License",
    "description": "FlowBitz is licensed under the MIT License. Learn about usage rights, commercial use, modification permissions, and third-party dependencies.",
    "url": "https://www.flowbitz.dev/license",
    "mainEntity": {
      "@type": "SoftwareApplication",
      "name": "FlowBitz",
      "license": "MIT",
      "url": "https://www.flowbitz.dev"
    }
  }

  return (
    <>
      <SEO 
        title={seoPages.license.title}
        description={seoPages.license.description}
        keywords={seoPages.license.keywords}
        image="https://www.slabpixel.dev/images/FlowBitz-OpenGraph.webp"
        url="https://www.flowbitz.dev/license"
        structuredData={structuredData}
      />
      <section className="bg-transparent text-foreground min-h-screen relative overflow-hidden">
        <div className="max-w-[1200px] lg:mx-auto mx-2 relative z-[2] border-x border-foreground/10">
          {/* Hero */}
          <header
            wb-component="smart-animate"
            className="w-full flex flex-col items-center gap-5 md:gap-[1.625rem] py-6 md:py-14 border-b border-foreground/10"
          >

            <h1 className="inter-semi-48 w-2/3 md:w-full text-center text-foreground">
              MIT License
            </h1>

            <p className="inter-reg-18 text-text-medium text-center px-4 md:px-0 max-w-[99%] md:max-w-[770px]">
              FlowBitz is released under the MIT License—use, modify, and distribute the motion
              system freely in personal and commercial Webflow projects.
            </p>
          </header>

          {/* License Content */}
          <div className="py-12 md:py-16 px-4 md:px-0 border-b border-foreground/10">
            <div className="max-w-4xl mx-auto space-y-[100px]">
              {/* License Terms Table */}
              <div className="group relative">
                <h3 className="inter-semi-24 text-foreground mb-4">License Terms</h3>
                <p className="text-paragraph large text-text-medium mb-6">
                  Complete overview of what you can do, requirements, and limitations under the MIT license
                </p>

                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-foreground/10 rounded-[6px] overflow-hidden">
                    <thead>
                      <tr className="bg-background/40">
                        <th className="border border-foreground/10 p-4 text-left font-semibold text-foreground">
                          Category
                        </th>
                        <th className="border border-foreground/10 p-4 text-left font-semibold text-foreground">
                          Item
                        </th>
                        <th className="border border-foreground/10 p-4 text-left font-semibold text-foreground">
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* What You Can Do */}
                      {allowedUses.map((use, index) => (
                        <tr key={`allowed-${index}`} className="hover:bg-foreground/5 transition-colors">
                          <td className="border border-foreground/10 p-4 text-sm text-foreground font-medium">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              <span className="text-green-600 dark:text-green-400">Allowed</span>
                            </div>
                          </td>
                          <td className="border border-foreground/10 p-4 text-sm font-semibold text-foreground">
                            {use.title}
                          </td>
                          <td className="border border-foreground/10 p-4 text-sm text-text-medium">
                            {use.description}
                          </td>
                        </tr>
                      ))}

                      {/* Requirements */}
                      {requirements.map((req, index) => (
                        <tr key={`requirement-${index}`} className="hover:bg-foreground/5 transition-colors">
                          <td className="border border-foreground/10 p-4 text-sm text-foreground font-medium">
                            <div className="flex items-center gap-2">
                              <Info className="w-4 h-4 text-blue-500" />
                              <span className="text-blue-600 dark:text-blue-400">Required</span>
                            </div>
                          </td>
                          <td className="border border-foreground/10 p-4 text-sm font-semibold text-foreground">
                            {req.title}
                          </td>
                          <td className="border border-foreground/10 p-4 text-sm text-text-medium">
                            {req.description}
                          </td>
                        </tr>
                      ))}

                      {/* Restrictions */}
                      {restrictions.map((restriction, index) => (
                        <tr key={`restriction-${index}`} className="hover:bg-foreground/5 transition-colors">
                          <td className="border border-foreground/10 p-4 text-sm text-foreground font-medium">
                            <div className="flex items-center gap-2">
                              <XCircle className="w-4 h-4 text-red-500" />
                              <span className="text-red-600 dark:text-red-400">Limited</span>
                            </div>
                          </td>
                          <td className="border border-foreground/10 p-4 text-sm font-semibold text-foreground">
                            {restriction.title}
                          </td>
                          <td className="border border-foreground/10 p-4 text-sm text-text-medium">
                            {restriction.description}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* License Text */}
              <div className="group relative">
                <h3 className="inter-semi-24 text-foreground mb-4">MIT License Text</h3>
                <p className="text-paragraph large text-text-medium mb-4">
                  The complete MIT License text for FlowBitz
                </p>

                <div className="relative">
                  <div className="bg-base-low border border-foreground/10 p-6 rounded-[6px]">
                    <pre className="text-sm whitespace-pre-wrap font-mono w-full">{mitLicense}</pre>
                  </div>
                </div>

                <p className="text-paragraph text-text-medium mt-4">
                  This license text should be included in any distribution of FlowBitz or modified versions.
                </p>
              </div>

              {/* Dependencies */}
              <div className="group relative">
                <h3 className="inter-semi-24 text-foreground mb-4">Third-Party Dependencies</h3>
                <p className="text-paragraph large text-text-medium mb-4">
                  FlowBitz uses several open-source libraries. Here's how their licenses affect you.
                </p>

                <div className="space-y-2">
                  {thirdPartyLicenses.map((dep, index) => (
                    <div
                      key={index}
                      className="border border-foreground/10 bg-base-low rounded-[6px] p-5 transition-all duration-300"
                    >
                      <div className="flex items-start justify-between mb-2 gap-4">
                        <div>
                          <h4 className="font-semibold text-foreground">{dep.name}</h4>
                          <p className="text-text-medium">Version {dep.version}</p>
                        </div>

                        <span className="inline-flex items-center px-3 py-1 rounded-[2px] border border-foreground/10 bg-background/40 text-text-medium text-xs font-semibold">
                          {dep.license}
                        </span>
                      </div>

                      <p className="mb-2 text-text-medium">{dep.description}</p>
                      <p className="text-sm text-text-medium opacity-80">{dep.note}</p>
                    </div>
                  ))}
                </div>

                <p className="text-paragraph text-text-medium mt-4">
                  Important Note: When using FlowBitz, you don't need to worry about individual library licenses.
                  All necessary licenses are included with FlowBitz. However, if you use these libraries directly
                  in your project outside of FlowBitz, you may need separate licenses.
                </p>
              </div>

              {/* FAQ */}
              <div className="group relative">
                <h3 className="inter-semi-24 text-foreground mb-4">
                  Frequently Asked Questions
                </h3>
                <p className="text-paragraph large text-text-medium mb-6">
                  Common questions about FlowBitz licensing and usage
                </p>

                <div className="space-y-3">
                  {faq.map((item, index) => (
                    <div
                      key={index}
                      className="bg-base-low border border-foreground/10 rounded-[6px] p-4 sm:p-6 hover:bg-foreground/5 transition-all duration-300"
                    >
                      <h4 className="text-lg font-semibold mb-2 text-foreground">{item.question}</h4>
                      <div className="prose prose-sm max-w-none text-text-medium">
                        <p className="whitespace-pre-line">{item.answer}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default License;
